import { useId, useState } from 'react'
import { useMutation } from '@tanstack/react-query'

import { maxTrialDateISO, minTrialDateISO, type TrialFormValues, trialSchema, utcDateISO } from './trialValidation'

import tabStyles from './ManageUserTab.module.scss'
import styles from '@/pages/app/Users/styles.module.scss'

import { isApiError } from '@/api'
import { type AdminUserLookupResponse, updateUserTrialEnd } from '@/api/internal'
import Button from '@/components/Button'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { useAnalytics } from '@/hooks/useAnalytics'
import { EventName } from '@/utils/eventDefinitions'

const SUBSCRIPTION_TRIAL_TOOLTIP = "This trial date comes from the user's subscription."
const MISSING_EMAIL_TOOLTIP = 'An email address is required to update the trial date.'

const TrialEndDateRow = ({
  user,
  formattedDate,
  onApplied,
}: {
  user: AdminUserLookupResponse
  formattedDate: string
  onApplied: () => Promise<void> | void
}) => {
  const inputId = useId()
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [editError, setEditError] = useState<string | null>(null)

  const exitEditMode = () => {
    setIsEditing(false)
    setEditValue('')
    setEditError(null)
  }

  const mutation = useMutation({
    mutationFn: (data: TrialFormValues) => updateUserTrialEnd(data),
    onSuccess: async (result) => {
      trackEvent?.(EventName.UpdatedUserTrial, {
        user_id: result.data.user_id,
        trial_ends_at: result.data.trial_ends_at,
      })
      const dt = new Date(result.data.trial_ends_at * 1000)
      updateToast(`Trial for ${result.data.user_email} now ends ${dt.toUTCString()}`, 'success')
      exitEditMode()
      try {
        await onApplied()
      } catch {
        // ManageUserTab reports lookup failures separately from the successful trial update.
      }
    },
    onError: (err) => {
      setEditError(isApiError(err) ? (err.detail ?? err.message) : 'Failed to update trial end date')
    },
  })

  const apply = () => {
    const parsed = trialSchema.safeParse({ user_email: user.email ?? '', trial_end: editValue })
    if (!parsed.success) {
      setEditError(parsed.error.issues[0].message)
      return
    }
    setEditError(null)
    mutation.mutate(parsed.data)
  }

  if (!isEditing) {
    const editDisabledReason =
      user.subscription_created_at != null ? SUBSCRIPTION_TRIAL_TOOLTIP : !user.email ? MISSING_EMAIL_TOOLTIP : ''
    const editButton = (
      <Button
        title="Edit"
        onClick={() => {
          setEditValue(user.trial_ends_at == null ? '' : utcDateISO(new Date(user.trial_ends_at * 1000)))
          setEditError(null)
          setIsEditing(true)
        }}
        type="light"
        className={tabStyles.rowAction}
        isDisabled={Boolean(editDisabledReason)}
      />
    )
    return (
      <div className={tabStyles.infoRow}>
        <span className={tabStyles.infoLabel}>Trial End Date</span>
        <span className={tabStyles.infoValue}>{formattedDate}</span>
        {editDisabledReason ? (
          <div className={tabStyles.rowActionSlot}>
            <Tooltip text={editDisabledReason} placement="top" hideArrow>
              {editButton}
            </Tooltip>
          </div>
        ) : (
          editButton
        )}
      </div>
    )
  }

  return (
    <div className={tabStyles.editRow}>
      <label className={tabStyles.infoLabel} htmlFor={inputId}>
        New Trial End Date (UTC)
      </label>
      <span id={hintId}>
        Trial ends at 23:59:59 UTC on the chosen date. Allowed: {minTrialDateISO()} through {maxTrialDateISO()}.
      </span>
      <div className={tabStyles.editControls}>
        <Input
          id={inputId}
          className={tabStyles.editInput}
          type="date"
          value={editValue}
          onInput={setEditValue}
          aria-describedby={editError ? `${hintId} ${errorId}` : hintId}
          aria-invalid={Boolean(editError)}
          isDisabled={mutation.isPending}
        />
        <Button
          title="Update"
          onClick={apply}
          type="dark"
          isLoading={mutation.isPending}
          isDisabled={mutation.isPending || !editValue}
        />
        <Button title="Cancel" onClick={exitEditMode} type="link" isDisabled={mutation.isPending} />
      </div>
      {editError && (
        <p id={errorId} className={styles.error} role="alert">
          {editError}
        </p>
      )}
    </div>
  )
}

export default TrialEndDateRow
